<template>
  <!-- 대시보드 상단 기간 필터 바. CertificateList.vue 기간 프리셋 패턴 재사용(REQ-MOD-1). -->
  <div class="search-filter-container dashboard-period-filter">
    <div class="search-filter-row">
      <div class="search-filter-title">
        {{ $t('common_searchfilter_period') }}
      </div>
      <div class="search-filter-content">
        <div class="date-filter-container">
          <div
            class="date-filter-item"
            :class="selectedDateFilter === 'all' ? 'selected' : ''"
            @click="handleDateFilter('all')"
          >
            {{ $t('common_total') }}
          </div>
          <div
            v-for="item in dateFilters"
            :key="item"
            class="date-filter-item"
            :class="selectedDateFilter === item ? 'selected' : ''"
            @click="handleDateFilter(item)"
          >
            {{ item === 'etc' ? $t('common_searchfilter_custom') : `${$t('common_searchfilter_recent')} ${convertMonthAndYearText(item)}` }}
          </div>
        </div>
        <div v-if="selectedDateFilter === 'etc'" class="date-selector-container custom">
          <a-range-picker
            :allowClear="false"
            size="small"
            :locale="locale"
            v-model:value="dateRanges"
            :disabled-date="disabledFutureDate"
            style="width: 250px; margin-left: 10px"
            @change="handleRangeChange"
          />
        </div>
      </div>
      <!-- REQ-1: 조회기간 바 우측 끝 엑셀 다운로드 버튼(margin-left:auto로 우측 정렬) -->
      <a-button
        class="dashboard-excel-download-btn"
        size="small"
        :disabled="downloading"
        :loading="downloading"
        @click="$emit('download')"
      >
        <i class="fa fa-download" />
        {{ $t('common_excel_download') }}
      </a-button>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import locale from 'ant-design-vue/es/date-picker/locale/ko_KR';

export default {
  name: 'DashboardPeriodFilter',
  emits: ['change', 'download'],
  props: {
    // localStorage 하이드레이션 값(dashboard-prefs.js가 이미 검증). 미지정 시 기존 기본값 유지.
    // { filter: 'all'|3|12|36|'etc', startAt?, endAt? }
    initialPeriod: { type: Object, default: null },
    // REQ-1 S-1.1: 다운로드 진행 중이거나 위젯 데이터가 준비되지 않은 동안 버튼 비활성화.
    downloading: { type: Boolean, default: false },
  },
  data() {
    const initial = this.initialPeriod;
    return {
      locale,
      // REQ-MOD-1 U-1.1: 기간 프리셋 [전체/3개월/1년/3년/직접선택]
      dateFilters: [3, 12, 36, 'etc'],
      // REQ-MOD-1 U-1.3: 최초 진입 기본 기간 = 1년(하이드레이션 값이 있으면 그 값을 우선)
      selectedDateFilter: initial ? initial.filter : 12,
      dateRanges: initial && initial.filter === 'etc'
        ? [dayjs(initial.startAt, 'YYYYMMDD'), dayjs(initial.endAt, 'YYYYMMDD')]
        : [],
    };
  },
  mounted() {
    // 하이드레이션된 초기값 그대로 최초 1회만 emit — 기본값으로 먼저 조회한 뒤
    // 다시 하이드레이션 값으로 재조회하는 이중 fetch를 방지한다.
    this.emitPeriod();
  },
  methods: {
    handleDateFilter(value) {
      if (value === this.selectedDateFilter) return;
      this.selectedDateFilter = value;
      if (value === 'etc') {
        // 직접선택 진입 시점에는 range picker 선택을 기다린다.
        return;
      }
      this.dateRanges = [];
      this.emitPeriod();
    },
    handleRangeChange() {
      this.emitPeriod();
    },
    // REQ-MOD-1 N-1.2: 미래 날짜 선택 차단
    disabledFutureDate(current) {
      return current && current > dayjs().endOf('day');
    },
    convertMonthAndYearText(input) {
      if (input < 12) return `${input}${this.$i18n.t('common_searchfilter_month')}`;
      return `${input / 12}${this.$i18n.t('common_searchfilter_year')}`;
    },
    emitPeriod() {
      // E-2.2: 칩 활성 상태는 즉시 전환되고(위 selectedDateFilter 변경), 데이터 로드는 비동기(부모가 처리)
      this.$emit('change', this.buildPeriod());
    },
    buildPeriod() {
      // filter 값을 함께 실어 보낸다 — Dashboard.vue가 localStorage에 "어떤 칩이 선택됐는지"를
      // 그대로 저장/복원할 수 있도록(startAt/endAt만으로는 3개월/12개월 프리셋을 역산할 수 없음).
      if (this.selectedDateFilter === 'all') {
        // O-1.1: "전체"는 startAt/endAt 생략
        return { filter: 'all', startAt: null, endAt: null };
      }
      if (this.selectedDateFilter === 'etc') {
        if (!this.dateRanges || this.dateRanges.length < 2 || !this.dateRanges[0] || !this.dateRanges[1]) {
          // N-1.1: 반쪽 기간으로 조회하지 않음 — 범위가 완성될 때까지 대기
          return null;
        }
        return {
          filter: 'etc',
          startAt: dayjs(this.dateRanges[0]).format('YYYYMMDD'),
          endAt: dayjs(this.dateRanges[1]).format('YYYYMMDD'),
        };
      }
      // E-1.1: dayjs().subtract(N, 'month') 기준 startAt, endAt=현재
      return {
        filter: this.selectedDateFilter,
        startAt: dayjs().subtract(this.selectedDateFilter, 'month').format('YYYYMMDD'),
        endAt: dayjs().format('YYYYMMDD'),
      };
    },
  },
};
</script>

<style scoped>
.dashboard-period-filter {
  margin-bottom: 16px;
}

/* 라벨("조회기간")과 칩 행의 수직 중앙 정렬을 명시적으로 보장 */
.dashboard-period-filter .search-filter-row {
  align-items: center;
}

.dashboard-period-filter .search-filter-title {
  align-items: center;
  justify-content: center;
}

.dashboard-period-filter .date-filter-container,
.dashboard-period-filter .search-filter-content {
  align-items: center;
}

/* REQ-1 U-1.1: 다운로드 버튼을 row 우측 끝으로 밀어낸다(기존 row는 우측 요소가 없었음). */
.dashboard-excel-download-btn {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
